/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Form helper utilities
 * Ported from lego/lib/Utils.js
 */

/**
 * Transform array of objects to Select options format
 * @param data - Array of objects
 * @param valueKey - Key to use for option value
 * @param labelKey - Key to use for option label
 * @returns Array of {value, label} objects
 */
export function transformToSelectOptions(
  data: any[],
  valueKey: string,
  labelKey: string
): Array<{ value: any; label: string }> {
  if (!Array.isArray(data)) {
    return []
  }

  return data.map((item) => ({
    value: item[valueKey],
    label: item[labelKey]
  }))
}

/**
 * Format textarea character counter
 * @param length - Current text length
 * @param max - Maximum allowed length
 * @param label - Label to display (e.g., "characters remaining")
 * @returns Formatted counter string
 */
export function formatTextAreaCounter(length: number, max: number, label: string): string {
  const remaining = max - length
  return `${remaining} ${label}`
}

/**
 * Deep clone an object
 * @param obj - Object to clone
 * @returns Cloned object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Check if array contains value
 * @param arr - Array to search
 * @param value - Value to find
 * @returns True if value is in array
 */
export function contains<T>(arr: T[], value: T): boolean {
  return arr.includes(value)
}

/**
 * Extend object with properties from other objects
 * @param target - Target object
 * @param sources - Source objects to copy from
 * @returns Extended target object
 */
export function extend<T extends Record<string, any>>(target: T, ...sources: Partial<T>[]): T {
  return Object.assign(target, ...sources)
}

/**
 * Generate unique ID
 * @param prefix - Optional prefix for ID
 * @returns Unique ID string
 */
let idCounter = 0
export function uniqueId(prefix = 'id'): string {
  idCounter++
  return `${prefix}_${idCounter}_${Date.now()}`
}

/**
 * Check if value is array
 * @param value - Value to check
 * @returns True if value is array
 */
export function isArray(value: any): value is any[] {
  return Array.isArray(value)
}

/**
 * Check if function
 * @param value - Value to check
 * @returns True if value is function
 */
export function isFunction(value: any): value is Function {
  return typeof value === 'function'
}

/**
 * For each iteration helper
 * @param collection - Array or object to iterate
 * @param iteratee - Function to call for each item
 */
export function forEach<T>(
  collection: T[] | Record<string, T>,
  iteratee: (value: T, key: string | number) => void
): void {
  if (Array.isArray(collection)) {
    collection.forEach(iteratee)
  } else if (typeof collection === 'object' && collection !== null) {
    Object.keys(collection).forEach((key) => {
      iteratee(collection[key], key)
    })
  }
}

/**
 * Filter array
 * @param collection - Array to filter
 * @param predicate - Filter function
 * @returns Filtered array
 */
export function filter<T>(collection: T[], predicate: (value: T) => boolean): T[] {
  return collection.filter(predicate)
}

/**
 * Check if object is empty
 * @param obj - Object to check
 * @returns True if object has no own properties
 */
export function isEmpty(obj: any): boolean {
  if (obj === null || obj === undefined) return true
  if (Array.isArray(obj) || typeof obj === 'string') return obj.length === 0
  if (typeof obj === 'object') return Object.keys(obj).length === 0
  return false
}

export const Utils = {
  transformToSelectOptions,
  formatTextAreaCounter,
  deepClone,
  contains,
  extend,
  uniqueId,
  isArray,
  isFunction,
  forEach,
  filter,
  isEmpty
}

export default Utils
